import {
    __BaseInputProps,
    ComboboxItem,
    ComboboxLikeProps,
    ComboboxLikeRenderOptionInput,
    ElementProps,
    Group,
    Text,
    getParsedComboboxData,
    getOptionsLockup,
    Combobox,
    ScrollAreaProps,
    ComboboxParsedItem,
    isOptionsGroup,
    ScrollArea,
} from '@mantine/core';
import { useId } from '@mantine/hooks';
import { useState, useEffect } from 'react';
import { Checkbox } from '../checkbox/Checkbox';
import baseStyles from './BaseFilterButton.module.css';
import menuStyles from '../styles/DropdownMenu.module.css';
import { Button } from '../button/Button';
import { BaseFilterButton, useBaseFilterButton } from './BaseFilterButton';

export type FilterButtonProps = {
    label?: string;
    disabled?: boolean;
    value?: string[];
    defaultValue?: string[];
    submittedValue?: string[];
    maxValues?: number;
    onChange?: (value: string[]) => void;
    onSubmit?: (value: string[]) => void;
    scrollAreaProps?: ScrollAreaProps;
    showSelectButtons?: boolean;
    showSubmitButtons?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    nothingFoundMessage?: string;
} & ComboboxLikeProps &
    __BaseInputProps &
    ElementProps<'input', 'size' | 'value' | 'defaultValue' | 'onChange'>;

export const FilterButton = ({
    label,
    disabled,
    value,
    defaultValue,
    submittedValue,
    onChange,
    onSubmit,
    data,
    form,
    size,
    maxValues = Infinity,
    comboboxProps,
    scrollAreaProps,
    maxDropdownHeight,
    showSelectButtons = false,
    showSubmitButtons = false,
    searchable = false,
    searchPlaceholder = 'Search...',
    nothingFoundMessage = 'No options found',
}: FilterButtonProps) => {
    const _id = useId();
    const [searchQuery, setSearchQuery] = useState('');

    const hooks = useBaseFilterButton<string[]>({
        value,
        defaultValue,
        submittedValue,
        onChange,
        onSubmit,
        finalValue: [],
    });

    const { value: _value, setValue, submittedValue: _submittedValue, combobox } = hooks;

    useEffect(() => {
        if (searchable && !combobox.dropdownOpened) {
            setSearchQuery('');
        }
    }, [combobox.dropdownOpened, searchable]);

    const parsedData = getParsedComboboxData(data);
    const optionsLockup = getOptionsLockup(parsedData);

    const filterOptions = (options: ComboboxParsedItem[], searchQuery: string): ComboboxParsedItem[] => {
        if (!searchQuery) return options;

        const matchesSearch = (label: string) => label.toLowerCase().includes(searchQuery.toLowerCase());

        return options
            .filter(option => {
                if (isOptionsGroup(option)) {
                    const filteredItems = option.items.filter(item => matchesSearch(item.label));
                    return filteredItems.length > 0 ? { ...option, items: filteredItems } : null;
                }
                return matchesSearch(option.label);
            })
            .filter(Boolean) as ComboboxParsedItem[];
    };

    const filteredData = searchable ? filterOptions(parsedData, searchQuery) : parsedData;

    const hasActiveFilters = _submittedValue.length > 0;

    const renderOption = ({ option, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
        return (
            <Combobox.Option
                value={option.value}
                key={option.value}
                data-checked={checked}
                aria-selected={checked}
            >
                <Group>
                    <Checkbox
                        checked={checked}
                        id={option.value}
                        readOnly
                        aria-readonly
                        aria-label={option.label}
                    />
                    <Text>{option.label}</Text>
                </Group>
            </Combobox.Option>
        );
    };

    const renderAllOptions = (parsedData: ComboboxParsedItem[]) => {
        if (parsedData.length === 0) {
            return searchable && searchQuery ? (
                <Combobox.Empty component={'option'}>{nothingFoundMessage}</Combobox.Empty>
            ) : null;
        }

        return parsedData.map(option => {
            if (isOptionsGroup(option)) {
                return (
                    <Combobox.Group key={option.group} label={option.group}>
                        {option.items.map(item =>
                            renderOption({
                                option: item,
                                checked: _value.includes(item.value),
                            })
                        )}
                    </Combobox.Group>
                );
            }
            return renderOption({
                option,
                checked: _value.includes(option.value),
            });
        });
    };

    const hiddenInputs = <Combobox.HiddenInput value={_value} form={form} disabled={disabled} />;

    const enhancedComboboxProps = {
        ...comboboxProps,
        onOptionSubmit: (val: string) => {
            combobox.updateSelectedOptionIndex('selected');
            if (_value.includes(optionsLockup[val]?.value ?? val)) {
                setValue(_value.filter(v => v !== optionsLockup[val]?.value));
            } else if (_value.length < maxValues!) {
                setValue([..._value, optionsLockup[val]?.value ?? val]);
            }
        },
        classNames: {
            ...comboboxProps?.classNames,
            option: menuStyles.option,
            dropdown: menuStyles.dropdown,
        },
    };

    return (
        <BaseFilterButton
            label={label || 'Filter'}
            disabled={disabled}
            size={size}
            showSubmitButtons={showSubmitButtons}
            comboboxProps={enhancedComboboxProps}
            hasActiveFilters={hasActiveFilters}
            badgeClassName={baseStyles.dot}
            hooks={hooks}
            hiddenInputs={hiddenInputs}
        >
            {searchable && (
                <Combobox.Search
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.currentTarget.value)}
                    placeholder={searchPlaceholder}
                />
            )}
            {showSelectButtons && (
                <Group align="center">
                    <Button
                        appearance="ghost"
                        size="compact-sm"
                        onClick={() => {
                            const allOptions = searchable && searchQuery ? filteredData : parsedData;
                            setValue(
                                allOptions.flatMap(option =>
                                    isOptionsGroup(option)
                                        ? option.items.map(item => item.value)
                                        : [option.value]
                                )
                            );
                        }}
                    >
                        Select All {searchable && searchQuery ? filteredData.length : data?.length}
                    </Button>
                    <Button
                        appearance="ghost"
                        size="compact-sm"
                        onClick={() => {
                            setValue([]);
                        }}
                    >
                        Clear All
                    </Button>
                </Group>
            )}

            <Combobox.Options labelledBy={_id} aria-label={label ?? 'filter-button-options'}>
                <ScrollArea.Autosize
                    mah={maxDropdownHeight ?? 220}
                    type="scroll"
                    scrollbarSize="var(--combobox-padding)"
                    offsetScrollbars="y"
                    {...scrollAreaProps}
                >
                    {renderAllOptions(filteredData)}
                </ScrollArea.Autosize>
            </Combobox.Options>
        </BaseFilterButton>
    );
};
